import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EbookDownloadProps {
  source: string;
  variant?: "primary" | "secondary";
}

export const EbookDownload = ({ source, variant = "primary" }: EbookDownloadProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Starting ebook download process for:", email);
      
      // First, store the email in the database
      const { error: dbError } = await supabase
        .from("email_subscriptions")
        .insert([{ email, source }]);

      if (dbError) {
        console.error("Database error:", dbError);
        throw new Error("Failed to save email subscription");
      }

      console.log("Email saved to database, sending ebook...");

      // Then, send the ebook via email
      const { data, error: functionError } = await supabase.functions.invoke('send-ebook', {
        body: { email, source }
      });

      if (functionError) {
        console.error("Edge function error:", functionError);
        throw new Error("Failed to send ebook");
      }

      console.log("Ebook sent successfully:", data);

      toast({
        title: "Success!",
        description: "Your free ebook has been sent to your email.",
      });
      setEmail("");
    } catch (error) {
      console.error("Error in ebook download process:", error);
      toast({
        title: "Error",
        description: error.message || "There was an error processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="flex-grow"
      />
      <Button 
        type="submit" 
        disabled={isLoading}
        variant={variant === "primary" ? "default" : "secondary"}
        className="whitespace-nowrap"
      >
        {isLoading ? "Sending..." : "Get Free Ebook"}
      </Button>
    </form>
  );
};